import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Lock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { images } from "../data/content";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/portal", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/portal", { replace: true });
    else setError(res.error);
  };

  const quickLogin = async (em, pw) => {
    setEmail(em);
    setPassword(pw);
    setLoading(true);
    setError("");
    const res = await login(em, pw);
    setLoading(false);
    if (res.ok) navigate("/portal", { replace: true });
    else setError(res.error);
  };

  const demoAccounts = [
    { role: "Worshipful Master", email: "wmaster@zetland508.org", pw: "Member1845!" },
    { role: "Secretary", email: "secretary@zetland508.org", pw: "Zetland1845!" },
    { role: "Almoner", email: "almoner@zetland508.org", pw: "Member1845!" },
    { role: "Treasurer", email: "treasurer@zetland508.org", pw: "Member1845!" },
    { role: "Member", email: "brother.read@zetland508.org", pw: "Member1845!" },
  ];

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-sapphire-deep px-6">
      <img src={images.templeInterior} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
      <div className="absolute inset-0 bg-sapphire-deep/75" />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 w-full max-w-md rounded-3xl border border-gold/25 bg-charcoal/70 p-9 backdrop-blur-xl"
        data-testid="login-card"
      >
        <Link to="/" className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-widest text-parchment/60 hover:text-gold">
          <ArrowLeft size={14} /> Back to site
        </Link>

        <div className="mt-6 text-center">
          <img src={images.crestSeal} alt="Lodge coat of arms" className="mx-auto h-20 w-20" />
          <h1 className="mt-4 font-display text-3xl font-semibold text-parchment">The Inner Lodge Portal</h1>
          <p className="mt-1 font-sans text-sm text-parchment/60">For verified Brethren of Lodge No. 508.</p>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
          <div>
            <label className="mb-2 block font-sans text-xs font-semibold uppercase tracking-widest text-gold">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 font-sans text-parchment outline-none transition focus:border-gold focus:ring-2 focus:ring-gold/30"
              data-testid="login-email"
            />
          </div>
          <div>
            <label className="mb-2 block font-sans text-xs font-semibold uppercase tracking-widest text-gold">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 font-sans text-parchment outline-none transition focus:border-gold focus:ring-2 focus:ring-gold/30"
              data-testid="login-password"
            />
          </div>

          {error && <p className="font-sans text-sm text-gold" data-testid="login-error">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-gold py-3 font-sans text-sm font-semibold uppercase tracking-widest text-sapphire-deep transition hover:bg-gold-light disabled:opacity-60"
            data-testid="login-submit"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={15} />}
            {loading ? "Signing in…" : "Enter the Portal"}
          </button>
        </form>

        <div className="mt-7 border-t border-white/10 pt-5" data-testid="quick-login">
          <p className="text-center font-sans text-[10px] font-semibold uppercase tracking-[0.25em] text-gold/80">
            Quick access — QA & demo
          </p>
          <div className="mt-3 flex flex-wrap justify-center gap-2">
            {demoAccounts.map((a) => (
              <button
                key={a.email}
                onClick={() => quickLogin(a.email, a.pw)}
                disabled={loading}
                className="rounded-full border border-gold/40 px-3 py-1.5 font-sans text-xs font-medium text-parchment/85 transition hover:bg-gold hover:text-sapphire-deep disabled:opacity-50"
                data-testid={`quick-login-${a.role.toLowerCase().replace(/\s/g, "-")}`}
              >
                {a.role}
              </button>
            ))}
          </div>
        </div>

        <p className="mt-6 text-center font-sans text-xs text-parchment/45">
          Access is granted to initiated members only. Contact the Secretary for credentials.
        </p>
      </motion.div>
    </div>
  );
}
